/*******************************************************************************
 * @ngdoc service
 * @name  csm.payments.services.service:DOMTools
 * @description
 * Tools for manipulating the browser Document Object Model.
 *******************************************************************************/
export function DomToolsService($injector) {
  this.createStylesObject = $injector.invoke(createStylesObject);
  this.getStyleSheetRules = $injector.invoke(getStyleSheetRules);
  this.getStyles = $injector.invoke(getStyles);
  this.loadStyleSheetRules = $injector.invoke(loadStyleSheetRules);
}

DomToolsService.$inject = ['$injector'];

/*******************************************************************************
 * DOMTools.createStylesObject */
/**
 * @ngdoc    function
 * @name     createStylesObject
 * @methodOf csm.payments.services.service:DOMTools
 * @param    {object} sheetRules      DOM stylesheet `rules` property.
 * @param    {string} selectorPrefix  Prefix of selectors to include in object.
 * @return   {object} Style rules converted to an object.
 * @description
 * Converts **`sheetRules`** with selectors that begin with **`selectorPrefix`**
 * to an object. This was originally created to read the styles from a sheet and
 * pass them to the OpenEdge Payfields API.
 *
 * **`sheetRules`** is an object keyed by numbers and each element is a `rule`
 * object where `rule.selectorText` is the selector for the rule. Only those
 * rules where `rule.selectorText` starts with **`selectorPrefix`** are included
 * in the returned object and `selectorPrefix` is removed from the selector.
 *
 * Each `rule` object included in the output is an object of style attributes
 * and values such that the resulting output looks like this:
 * ```
 *   {
 *   "selector": { "attribute":"value", "attribute":"value", ... },
 *   "selector": { "attribute":"value", "attribute":"value", ... },
 *   ...
 *   }
 * ```
 *******************************************************************************/
/* @ngInject */ function createStylesObject() {
  return function (sheetRules, selectorPrefix) {
    /*-------------------------------------------------------------------*/
    /* Find the CSS rules with `rule.selectorText` starting with         */
    /* `selectorPrefix`.                                                 */
    /*                                                                   */
    /* Note: This ignores media queries (selectorText == null), for now. */
    /*-------------------------------------------------------------------*/
    var rules = [].filter.call(sheetRules, function (rule) {
      return (rule.selectorText || '').indexOf(selectorPrefix) == 0;
    });
    if (rules.length == 0)
      throw "No CSS rules found starting with '" + selectorPrefix + "'.";

    /*----------------------------------------------------------*/
    /* Escape regex characters in `selectorPrefix` and create a */
    /* regex to remove the prefix from the CSS rule selectors.  */
    /*                                                          */
    /* https://stackoverflow.com/a/6969486/2245849              */
    /*----------------------------------------------------------*/
    var reSelector = new RegExp(
      selectorPrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s+',
      'g'
    );

    /*----------------------------------------------*/
    /* Return styles object created from CSS rules. */
    /*----------------------------------------------*/
    var styles = rules.reduce(function (styles, rule) {
      /*------------------------------------------------*/
      /* Create attributes object from rule attributes. */
      /*------------------------------------------------*/
      var attrs = [].reduce.call(
        rule.style,
        function (attrs, style) {
          attrs[style] = rule.style.getPropertyValue(style);
          return attrs;
        },
        {}
      );

      /*-----------------------------------*/
      /* Return if rule has no attributes. */
      /*-----------------------------------*/
      if (angular.equals(attrs, {})) return styles;

      /*---------------------------------------------------*/
      /* Convert `background-repeat-x|y` to                */
      /* `background-repeat` for browser support (Safari). */
      /*---------------------------------------------------*/
      var brx = attrs['background-repeat-x'];
      var bry = attrs['background-repeat-y'];

      if (brx || bry)
        attrs['background-repeat'] = (brx ? brx + ' ' : '') + (bry || '');

      /*------------------------------------------------*/
      /* Remove instances of `selectorPrefix` from rule */
      /* selector and set rule attributes for selector. */
      /*------------------------------------------------*/
      var selector = rule.selectorText.replace(reSelector, '');
      styles[selector] = attrs;

      return styles;
    }, {});

    return styles;
  };
}

/*******************************************************************************
 * DOMTools.getStyles */
/**
 * @ngdoc    function
 * @name     getStyles
 * @methodOf csm.payments.services.service:DOMTools
 * @param    {string} sheetName       Stylesheet name.
 * @param    {string} selectorPrefix  Prefix of selectors to include in object.
 * @return   {object} Style rules converted to an object.
 * @description
 * Loads the style rules from `document.styleSheets` for the sheet with the path
 * ending in **`sheetName`** and returns a call to `getStyleSheetRules()` with
 * **`selectorPrefix`**.
 *******************************************************************************/
/* @ngInject */ function getStyles() {
  return function (sheetName, selectorPrefix) {
    return this.createStylesObject(
      this.getStyleSheetRules(sheetName),
      selectorPrefix
    );
  };
}

/*******************************************************************************
 * DOMTools.getStyleSheetRules */
/**
 * @ngdoc    function
 * @name     getStyleSheetRules
 * @methodOf csm.payments.services.service:DOMTools
 * @param    {string} sheetName       Stylesheet name.
 * @param    {string} selectorPrefix  Prefix of selectors to include in object.
 * @return   {object} Style rules from **`sheetName`** stylesheet.
 * @description
 * Returns the style rules from `document.styleSheets` for the sheet with the
 * path ending in **`sheetName`**.
 *******************************************************************************/
/* @ngInject */ function getStyleSheetRules() {
  return function (sheetName) {
    /*-------------------------------------------------------*/
    /* Find the style sheet. `document.styleSheets` is an    */
    /* object keyed by numbers so we can use `Array.filter`. */
    /*-------------------------------------------------------*/
    var sheet = [].filter
      .call(document.styleSheets, function (sheet) {
        return (sheet.href || '').indexOf(sheetName) > 0;
      })
      .pop();
    if (sheet == null) throw "Style sheet '" + sheetName + "' not found.";

    return sheet.rules;
  };
}

/*******************************************************************************
 * DOMTools.loadStyleSheetRules */
/**
 * @ngdoc    function
 * @name     loadStyleSheetRules
 * @methodOf csm.payments.services.service:DOMTools
 * @param    {string} sheetURL  Stylesheet URL.
 * @return   {object} Style rules from **`sheetURL`** stylesheet.
 * @description
 * Loads the stylesheet from **`sheetURL`** and returns the style rules object.
 *******************************************************************************/
/* @ngInject */ function loadStyleSheetRules($http, $q) {
  return function (sheetURL) {
    return $q(function (resolve) {
      $http.get(sheetURL).then(
        function (r) {
          var doc = document.implementation.createHTMLDocument('');
          var style = document.createElement('style');
          style.textContent = r.data;
          doc.body.appendChild(style);

          resolve(style.sheet.rules);
        },
        function () {
          throw "Style sheet '" + sheetURL + "' could not be loaded.";
        }
      );
    });
  };
}
